import React, { useState } from 'react';
import { estimatesAPI, companiesAPI } from '../services/api';
import { useApi } from '../hooks/useApi';
import { Estimate, Company } from '../types';
import { 
  Plus, 
  Search, 
  Calculator, 
  Eye, 
  Edit, 
  Trash2,
  Send,
  AlertCircle,
  X
} from 'lucide-react';

interface EstimateItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

const Estimates: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState<Estimate | null>(null);
  const [viewingEstimate, setViewingEstimate] = useState<Estimate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { data: estimates, loading, error, refetch } = useApi<Estimate[]>(
    () => estimatesAPI.getAll(),
    []
  );

  const { data: companies } = useApi<Company[]>(
    () => companiesAPI.getAll(),
    []
  );

  const [newEstimate, setNewEstimate] = useState({
    company_id: '',
    estimate_number: '',
    issue_date: '',
    expiry_date: '',
    subtotal: '',
    tax_rate: '0',
    discount_amount: '0',
    notes: '',
    terms: '',
    status: 'draft' as 'draft' | 'sent' | 'accepted' | 'declined' | 'expired'
  });

  const [items, setItems] = useState<EstimateItem[]>([
    { description: '', quantity: 1, rate: 0, amount: 0 }
  ]);

  const generateEstimateNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `EST-${year}${month}-${random}`;
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof EstimateItem, value: string | number) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Calculate amount for this item
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    }
    
    setItems(updatedItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const taxRate = parseFloat(newEstimate.tax_rate) || 0;
    const discountAmount = parseFloat(newEstimate.discount_amount) || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount - discountAmount;
    
    return { subtotal, taxAmount, totalAmount };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-primary-100 text-primary-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEstimates = estimates?.filter(estimate => {
    const matchesSearch = estimate.estimate_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         estimate.company?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || estimate.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  const handleView = async (estimate: Estimate) => {
    try {
      const response = await estimatesAPI.getById(estimate.id);
      setViewingEstimate(response.data.data); // Access the nested data
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching estimate details:', error);
      alert('Error loading estimate details. Please try again.');
    }
  };

  const handleSend = async (estimate: Estimate) => {
    if (window.confirm('Are you sure you want to send this estimate?')) {
      try {
        setIsSubmitting(true);
        await estimatesAPI.send(estimate.id);
        refetch();
        alert('Estimate sent successfully!');
      } catch (error) {
        console.error('Error sending estimate:', error);
        alert('Error sending estimate. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that we have at least one item with description
    const validItems = items.filter(item => item.description.trim() !== '');
    if (validItems.length === 0) {
      alert('Please add at least one item with a description.');
      return;
    }

    try {
      setIsSubmitting(true);
      const { subtotal, taxAmount, totalAmount } = calculateTotals();

      const estimateData = {
        ...newEstimate,
        company_id: parseInt(newEstimate.company_id),
        subtotal,
        tax_rate: parseFloat(newEstimate.tax_rate),
        tax_amount: taxAmount,
        discount_amount: parseFloat(newEstimate.discount_amount),
        total_amount: totalAmount,
        currency: 'GBP',
        items: validItems
      };

      if (editingEstimate) {
        await estimatesAPI.update(editingEstimate.id, estimateData);
      } else {
        await estimatesAPI.create(estimateData);
      }
      
      setShowAddModal(false);
      setEditingEstimate(null);
      resetForm();
      refetch();
    } catch (error) {
      console.error('Error saving estimate:', error);
      alert('Error saving estimate. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewEstimate({
      company_id: '',
      estimate_number: '',
      issue_date: '',
      expiry_date: '',
      subtotal: '',
      tax_rate: '0',
      discount_amount: '0',
      notes: '',
      terms: '',
      status: 'draft'
    });
    setItems([{ description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const handleEdit = async (estimate: Estimate) => {
    try {
      setIsSubmitting(true);
      // Fetch full estimate details with items
      const response = await estimatesAPI.getById(estimate.id);
      const fullEstimate = response.data.data; // Access the nested data
      
      setEditingEstimate(fullEstimate);
      setNewEstimate({
        company_id: fullEstimate.company_id.toString(),
        estimate_number: fullEstimate.estimate_number,
        issue_date: fullEstimate.issue_date.split('T')[0],
        expiry_date: fullEstimate.expiry_date.split('T')[0],
        subtotal: fullEstimate.subtotal?.toString() || '0',
        tax_rate: fullEstimate.tax_rate?.toString() || '0',
        discount_amount: fullEstimate.discount_amount?.toString() || '0',
        notes: fullEstimate.notes || '',
        terms: fullEstimate.terms || '',
        status: fullEstimate.status
      });
      
      // Set items from the full estimate data
      if (fullEstimate.items && fullEstimate.items.length > 0) {
        setItems(fullEstimate.items.map((item: import('../types').EstimateItem) => ({
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          amount: item.quantity * item.rate
        })));
      } else {
        setItems([{ description: '', quantity: 1, rate: 0, amount: 0 }]);
      }
      
      setShowAddModal(true);
    } catch (error) {
      console.error('Error fetching estimate for edit:', error);
      alert('Error loading estimate details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this estimate?')) {
      try {
        await estimatesAPI.delete(id);
        refetch();
      } catch (error) {
        console.error('Error deleting estimate:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading estimates</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Estimates</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage estimates for your potential clients.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              setNewEstimate(prev => ({
                ...prev,
                estimate_number: generateEstimateNumber(),
                issue_date: new Date().toISOString().split('T')[0],
                expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
              }));
              setShowAddModal(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Estimate
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="sm:flex sm:items-center sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Search estimates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-3 sm:mt-0">
              <select
                className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Estimates Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {filteredEstimates.length === 0 ? (
            <div className="text-center py-12">
              <Calculator className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No estimates</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new estimate.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setNewEstimate(prev => ({
                      ...prev,
                      estimate_number: generateEstimateNumber(),
                      issue_date: new Date().toISOString().split('T')[0],
                      expiry_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                    }));
                    setShowAddModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Estimate
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estimate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiry Date
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEstimates.map((estimate) => (
                    <tr key={estimate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {estimate.estimate_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(estimate.issue_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {estimate.company?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          £{estimate.total_amount.toLocaleString('en-GB')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(estimate.status)}`}>
                          {estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(estimate.expiry_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleView(estimate)}
                            className="text-primary-600 hover:text-primary-900"
                            title="View estimate"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleEdit(estimate)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit estimate"
                            disabled={isSubmitting}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleSend(estimate)}
                            className="text-green-600 hover:text-green-900"
                            title="Send estimate"
                            disabled={isSubmitting || estimate.status === 'sent'}
                          >
                            <Send className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(estimate.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete estimate"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View Estimate Modal */}
      {showViewModal && viewingEstimate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Estimate Details
                      </h3>
                      <button
                        type="button"
                        onClick={() => {
                          setShowViewModal(false);
                          setViewingEstimate(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>
                    
                    <div className="space-y-6">
                      {/* Estimate Header */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium text-gray-900">Estimate #{viewingEstimate.estimate_number}</h4>
                            <p className="text-sm text-gray-600">Company: {viewingEstimate.company?.name || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Issue Date: {new Date(viewingEstimate.issue_date).toLocaleDateString()}</p>
                            <p className="text-sm text-gray-600">Expiry Date: {new Date(viewingEstimate.expiry_date).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(viewingEstimate.status)}`}>
                              {viewingEstimate.status.charAt(0).toUpperCase() + viewingEstimate.status.slice(1)}
                            </span>
                            <p className="text-2xl font-bold text-gray-900 mt-2">£{viewingEstimate.total_amount.toLocaleString('en-GB')}</p>
                          </div>
                        </div>
                      </div>

                      {/* Items */}
                      {viewingEstimate.items && viewingEstimate.items.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3">Line Items</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {viewingEstimate.items.map((item, index) => (
                                  <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${item.rate.toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${(item.quantity * item.rate).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Totals */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span>${viewingEstimate.subtotal?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Tax ({viewingEstimate.tax_rate || 0}%):</span>
                            <span>${viewingEstimate.tax_amount?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Discount:</span>
                            <span>-${viewingEstimate.discount_amount?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between font-bold text-lg border-t pt-2">
                            <span>Total:</span>
                            <span>${viewingEstimate.total_amount.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Notes and Terms */}
                      {(viewingEstimate.notes || viewingEstimate.terms) && (
                        <div className="space-y-4">
                          {viewingEstimate.notes && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{viewingEstimate.notes}</p>
                            </div>
                          )}
                          {viewingEstimate.terms && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-2">Terms & Conditions</h4>
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{viewingEstimate.terms}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingEstimate(null);
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Estimate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          {editingEstimate ? 'Edit Estimate' : 'Create New Estimate'}
                        </h3>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddModal(false);
                            setEditingEstimate(null);
                            resetForm();
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-6 w-6" />
                        </button>
                      </div>
                      
                      <div className="space-y-6">
                        {/* Basic Information */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Company *
                            </label>
                            <select
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEstimate.company_id}
                              onChange={(e) => setNewEstimate({ ...newEstimate, company_id: e.target.value })}
                            >
                              <option value="">Select company</option>
                              {companies?.map(company => (
                                <option key={company.id} value={company.id}>
                                  {company.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Estimate Number *
                            </label>
                            <input
                              type="text"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEstimate.estimate_number}
                              onChange={(e) => setNewEstimate({ ...newEstimate, estimate_number: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Issue Date *
                            </label>
                            <input
                              type="date"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEstimate.issue_date}
                              onChange={(e) => setNewEstimate({ ...newEstimate, issue_date: e.target.value })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Expiry Date *
                            </label>
                            <input
                              type="date"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              value={newEstimate.expiry_date}
                              onChange={(e) => setNewEstimate({ ...newEstimate, expiry_date: e.target.value })}
                            />
                          </div>
                        </div>

                        {/* Line Items */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-gray-700">
                              Line Items *
                            </label>
                            <button
                              type="button"
                              onClick={addItem}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add Item
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            {items.map((item, index) => (
                              <div key={index} className="grid grid-cols-12 gap-3 items-end">
                                <div className="col-span-5">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    required
                                    placeholder="Item description"
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    value={item.description}
                                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Qty
                                  </label>
                                  <input
                                    type="number"
                                    required
                                    min="0.01"
                                    step="0.01"
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    value={item.quantity}
                                    onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Rate
                                  </label>
                                  <input
                                    type="number"
                                    required
                                    min="0.01"
                                    step="0.01"
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    value={item.rate}
                                    onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Amount
                                  </label>
                                  <input
                                    type="text"
                                    readOnly
                                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm"
                                    value={`$${item.amount.toFixed(2)}`}
                                  />
                                </div>
                                <div className="col-span-1">
                                  <button
                                    type="button"
                                    onClick={() => removeItem(index)}
                                    disabled={items.length === 1}
                                    className="inline-flex items-center justify-center w-8 h-8 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Totals */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Tax Rate (%)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                value={newEstimate.tax_rate}
                                onChange={(e) => setNewEstimate({ ...newEstimate, tax_rate: e.target.value })}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Discount Amount
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                value={newEstimate.discount_amount}
                                onChange={(e) => setNewEstimate({ ...newEstimate, discount_amount: e.target.value })}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700">
                                Status
                              </label>
                              <select
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                value={newEstimate.status}
                                onChange={(e) => setNewEstimate({ ...newEstimate, status: e.target.value as any })}
                              >
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="accepted">Accepted</option>
                                <option value="declined">Declined</option>
                                <option value="expired">Expired</option>
                              </select>
                            </div>
                          </div>

                          {/* Total Summary */}
                          <div className="mt-4 border-t pt-4">
                            <div className="flex justify-between items-center text-sm">
                              <span>Subtotal:</span>
                              <span>${calculateTotals().subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span>Tax:</span>
                              <span>${calculateTotals().taxAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span>Discount:</span>
                              <span>-${parseFloat(newEstimate.discount_amount || '0').toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-lg font-semibold border-t pt-2 mt-2">
                              <span>Total:</span>
                              <span>${calculateTotals().totalAmount.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Notes
                          </label>
                          <textarea
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={newEstimate.notes}
                            onChange={(e) => setNewEstimate({ ...newEstimate, notes: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Terms & Conditions
                          </label>
                          <textarea
                            rows={3}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={newEstimate.terms}
                            onChange={(e) => setNewEstimate({ ...newEstimate, terms: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : (editingEstimate ? 'Update' : 'Create')} Estimate
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingEstimate(null);
                      resetForm();
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Estimates; 